import jwtDecode, { JwtPayload } from 'jwt-decode'
import lodash from 'lodash'
import { action, autorun, computed, observable, runInAction, toJS } from 'mobx'
import moment from 'moment'
import { gotoPractice } from '../../helpers/gotoPractice'
import $global from '../../store/global'
import { BackupKeys } from './consts'
import { AjaxBasics } from '@xt/client/helpers'
import { BaseCouponItem } from '@xt/client/types/coupon'
import singleToken from '@xt/client/experimental/request/token'
import type { AccountGiftUIData } from '@xt/client/entities/my-v2/types.d'
import type { GiftsResponse } from '@xt/client/entities/user/types.d'
import { EnumProductType } from '@xt/client/enums/product'

type LogoutType = 'notice' | 'condition' | 'auth' | 'account' | 'abandon'
type AbandonType = 'course' | 'resource'

export type ModalState = 'phone' | 'email' | 'noBackPhone' | 'noBackEmail'

declare var zc: any
export default class EntitiesUser {
  constructor() {
    autorun(() => {
      const redirectBackUrl = sessionStorage.getItem('__redirectBackUrl')
      if (this.isLogin() && redirectBackUrl) {
        sessionStorage.removeItem('__redirectBackUrl')
        const url = new URL(redirectBackUrl)
        gotoPractice({
          url: url.toString(),
          tokens: singleToken.getToken()
        })
      }
    })
  }
  /**
   * 加载状态
   * @memberof Pagination
   */
  @observable
  loading = false
  /**
   * 显示登录框
   * @memberof Pagination
   */
  @observable
  visible = false

  /**
   * 在PC登录弹窗上层覆盖一个loading蒙层
   * 当前用户拖动验证码成功后立刻关闭并异步调用接口，防止重复调用接口
   * @type {boolean}
   */
  @observable
  isLoginModalLoading = false

  /**
   * 用来阻止登录框弹出，检测一次后会重置为false
   */
  @observable
  preventVisible = false

  /**
   * 登录框展示内容
   * @memberof Pagination
   */
  @observable
  ModalTitle = ''
  /**
   * 登录框状态 phone 手机验证 email 邮箱验证 noBackPhone 不可返回的手机验证 noBackEmail 不可返回的邮箱验证
   * @memberof Pagination
   */
  @observable
  ModalState = 'phone'

  /**
   * 用户信息
   * @memberof EntitiesStationery
   */
  @observable
  UserInfo: any = {}

  /**
   * 用户的收货地址
   */
  @observable
  userAddress: {
    id: string | null
    city: string | null
    detailAddress: string | null
    district: string | null
    nickname: string | null
    phoneNum: string | null
    postalCode: string | null
    province: string | null
    street: string | null
  } | null = null

  @computed
  get userAddressText() {
    return this.userAddress && this.userAddress.province && this.userAddress.city && this.userAddress.district
      ? this.userAddress.province + this.userAddress.city + this.userAddress.district
      : ''
  }

  @observable
  repage: string | null

  @action
  public usedRepage(href: string) {
    let repage = this.repage || href
    this.repage = null
    return repage
  }

  @action.bound
  setWXCheckTime(time: number) {
    this.UserInfo.wxCheckTime = time
  }

  @action.bound
  setWXNickname(value: string) {
    this.UserInfo.wxNickname = value
  }

  @action.bound
  clearUserInfo() {
    this.UserInfo = {}
  }

  // 新版传参只有userInfo，没有token信息
  @action.bound
  setUserInfo(info) {
    const _info = lodash.assign({}, this.UserInfo ?? {}, info)
    this.UserInfo = _info

    if ($global.platform === 'Mobile') {
      switch (sessionStorage.getItem('__xt__embedded__source')) {
        case 'uc':
          localStorage.setItem(BackupKeys.user_uc_backup, JSON.stringify({ UserInfo: _info }))
          break
        case 'show':
          localStorage.setItem(BackupKeys.user_show_backup, JSON.stringify({ UserInfo: _info }))
          break
        default:
          localStorage.setItem(BackupKeys.user_h5_backup, JSON.stringify({ UserInfo: _info }))
          break
      }
    }
  }
  /**
   * 设置智齿客服的用户信息
   * @param info
   */
  protected setZhichiUser(info) {
    if (!window['zc']) return
    zc('config', {
      partnerid: info.id, //客户id
      uname: info.nickname, //客户名称
      realname: info.nickname, //客户真实姓名
      tel: info.phoneNum, //客户电话,
      email: info.email, //客户邮箱
      face: info.headerUrl, //客户头像
      remark: '用户已登录' //客户备注信息
    })
    window.onmessage = e => {
      if (e && e.data) {
        // 此处JSON.parse可能会有问题
        // Unexpected token 's', "setImmedia"... is not valid JSON
        try {
          const { name, data } = lodash.isString(e.data) ? JSON.parse(e.data) : e.data
          if (name === 'zc_post_message' && data.action === 'chat_collapse_window') {
            zc('config', {
              refresh: false // 每次打开都刷新
            })
            window.onmessage = () => {}
          }
        } catch {}
      }
    }
  }

  @computed
  get isWxBind() {
    return !lodash.isEmpty(lodash.get(this.UserInfo, 'wxNickname'))
  }

  @computed
  get wxCheckTime() {
    return lodash.get(this.UserInfo, 'wxCheckTime')
  }

  /**
   * 设置 微信 wxOpenid
   * @param wxOpenid
   */
  @action.bound
  setWXOpenid(wxOpenid) {
    this.UserInfo = lodash.merge({ wxOpenid }, toJS(this.UserInfo))
  }
  /**
   * 已购买 的课程 ids
   * @type {Array<any>}
   * @memberof EntitiesUser
   */
  @observable CourseListHasbuy: Array<any> = []
  @observable CourseGroupHasbuy: Array<any> = []

  /**
   * 我的礼物列表
   * @type {Array<GiftsResponse>}
   * @memberof EntitiesUser
   */
  @observable OrderGifts: Array<GiftsResponse> = []
  @observable OrderGiftsData: Array<GiftsResponse> = []
  @observable OrderGiftsItem: GiftsResponse | null = null

  get AccountGiftsData(): Array<AccountGiftUIData> {
    return this.transformOrderGiftsData(this.OrderGiftsData)
  }

  showOrderGiftsItem(item: GiftsResponse) {
    const { orderNo } = item
    const key: string = `gift-${orderNo!}`
    localStorage.removeItem(key)
    this.setOrderGiftsItem(item)
  }

  transformOrderGiftsData(list: Array<GiftsResponse>) {
    return list.map(item => {
      let unit: string = '节'

      switch (item.productType) {
        case EnumProductType.single:
        case EnumProductType.fullstage:
          unit = '节'
          break
        case EnumProductType.dayCourse:
        case EnumProductType.selfStudy:
          unit = '天'
          break
        case EnumProductType.onlineExhibition:
        case EnumProductType.offlineExhibition:
          unit = '关'
          break
      }

      return {
        ...item,
        unit
      }
    })
  }

  @action.bound
  protected setOrderGiftsItem(item: GiftsResponse | null) {
    this.OrderGiftsItem = item
  }

  /**
   * 用户获得的未读优惠券列表
   */
  @observable UserReceivedCouponList: Array<BaseCouponItem> = []

  @action.bound
  protected setCourseListHasbuy(list, groupHasBuy) {
    this.CourseListHasbuy = list
    this.CourseGroupHasbuy = groupHasBuy
  }

  @action.bound
  protected setOrderGifts(list) {
    this.OrderGifts = [...list]
  }

  @action.bound
  public setUserReceivedCouponList(list) {
    this.UserReceivedCouponList = list
  }

  @action.bound
  public clearUserReceivedCouponList() {
    this.UserReceivedCouponList = []
  }

  @action
  removeOrderGifts(id) {
    const OrderGifts = [...this.OrderGifts]
    lodash.remove(OrderGifts, item => item.id == id)
    this.setOrderGifts(OrderGifts)
    // 单独隐藏指定显示的OrderGifts
    if (this.OrderGiftsItem) {
      this.setOrderGiftsItem(null)
    }
  }

  @action
  removeAllOrderGifts() {
    this.setOrderGifts([])
  }

  @action.bound
  protected setOrderGiftsData(list) {
    this.OrderGiftsData = [...list]
  }

  @action
  removeOrderGiftsData(id) {
    const OrderGiftsData = [...this.OrderGiftsData]
    lodash.remove(OrderGiftsData, item => item.id === id)
    this.setOrderGiftsData(OrderGiftsData)
  }

  // TODO: 之后要把优惠券待确认和礼物放到uniform里面
  @observable couponRefundNotice: any[] = []
  @observable couponRefundNoticeData: any[] = []
  @action.bound
  protected setCouponRefundNotice(list) {
    this.couponRefundNotice = [...list]
  }
  @action
  removeCouponRefundNotice(couponId: number) {
    const refundNoticeList = [...this.couponRefundNotice]
    lodash.remove(refundNoticeList, item => item.couponId === couponId)
    this.setCouponRefundNotice(refundNoticeList)
  }
  @action.bound
  protected setCouponRefundNoticeData(list) {
    this.couponRefundNoticeData = [...list]
  }
  @action
  removeCouponRefundNoticeData(couponId: number) {
    const refundNoticeList = [...this.couponRefundNoticeData]
    lodash.remove(refundNoticeList, item => item.couponId == couponId)
    this.setCouponRefundNoticeData(refundNoticeList)
  }

  @observable claimedGift: string[] = []

  @action.bound
  protected setClaimedGift(list) {
    this.claimedGift = [...list]
  }

  @observable uniformGift: Array<any> = []
  @observable uniformGiftData: Array<any> = []

  @action.bound
  protected setUniformGift(list) {
    this.uniformGift = [...list]
  }

  @action
  removeUniformGift() {
    this.uniformGift.shift()
  }

  @action.bound
  protected setUniformGiftData(list) {
    this.uniformGiftData = [...list]
  }

  @action
  removeUniformGiftData() {
    this.uniformGiftData.shift()
  }

  /**
   * 微信 openid
   * @readonly
   * @memberof EntitiesUser
   */
  @computed
  get wxOpenid() {
    return this.UserInfo.wxOpenid
  }
  /**
   * 桐板儿
   * @readonly
   * @memberof EntitiesUser
   */
  @computed
  get copper() {
    return this.UserInfo.coin
  }
  /**
   * 登录状态
   * @readonly
   * @memberof EntitiesUser
   * @deprecated
   */
  @computed
  get loggedIn() {
    return !!singleToken.getToken()?.access_token
  }

  isLogin() {
    return !!singleToken.getToken()?.access_token
  }

  /**
   * 解密的 jwt 信息
   * @readonly
   * @memberof EntitiesUser
   */
  @computed
  get JwtPayload() {
    if (this.isLogin()) {
      return jwtDecode<
        JwtPayload & {
          authorities: Array<String>
          client_id: string
          user_name: string
          auth_type: string
        }
      >(singleToken.getToken()!.access_token)
    }
  }
  /**
   * 过期时间
   * @readonly
   * @memberof EntitiesUser
   */
  @computed
  get expirationTime() {
    const exp = lodash.get(this.JwtPayload, 'exp', 0)
    if (exp) {
      return moment(exp * 1000)
    }
    return AjaxBasics.serviceDate.utcOffset(+8)
  }
  /**
   * 特殊协议
   * @readonly
   * @memberof EntitiesUser
   */
  @computed
  get visibleSpecialAgreement() {
    return this.UserInfo.specialProtocolId === 0
  }

  /**
   * 是否需要绑定设备
   * @readonly
   * @memberof EntitiesUser
   */
  @computed
  get needBindDevice() {
    return !lodash.get(this.UserInfo, 'email') && !lodash.get(this.UserInfo, 'phoneNum')
  }

  /**
   * 等级
   */
  @computed
  get level() {
    return this.UserInfo.level || 0
  }
  /**
   * 精进值
   */
  @computed
  get count() {
    return this.UserInfo.count || 0
  }
  @observable progress: number = 0
  sumexperience = [0, 200, 450, 800, 1300, 2000, 3000, 4500, 7000, 12500, 22500]
  upgrade = [0, 200, 250, 350, 500, 700, 1000, 1500, 2500, 5500, 10000]
  lvname = ['朗空', '精进', '闲趣', '得乐', '明诚', '和靖', '雅安', '广博', '翰墨', '妙微', '通会']

  @action updateProgress() {
    this.progress = ((this.count - this.sumexperience[this.level]) / this.upgrade[this.level + 1]) * 100
  }

  /**
   * 是否需要完善个人信息
   * @readonly
   * @memberof EntitiesUser
   */
  // @computed
  onneedCompleteInfo() {
    let info = lodash.pick(this.UserInfo, [
      'sex', //性别(0 未知 1 男 2女)
      'birthday', //生日
      'industry', //行业
      'career', //职业
      'education', //学历
      'location' //国家
    ])
    return lodash.some(lodash.values(info), item => {
      return lodash.includes(['', 0, null, undefined], item)
    })
    // return true
  }

  /**
   * 切换加载状态
   * @private
   * @param {boolean} [loading=!this.loading]
   * @memberof Pagination
   */
  @action
  protected onToggleLoading(loading: boolean = !this.loading) {
    this.loading = loading
  }
  /**
   * 切换登录框显示
   * @param visible
   */
  @action
  onToggleVisible(visible: boolean = !this.visible, repage: string | null = null) {
    if (visible && this.isLogin()) {
      return
    }
    this.repage = repage
    if (visible && this.preventVisible) {
      this.preventVisible = false
    } else {
      this.visible = visible
    }

    // h5登录的popup组件有0.3秒的动画
    setTimeout(
      () => {
        runInAction(() => {
          this.ModalTitle = 'links_signin'
        })
      },
      $global.platform === 'Mobile' ? 500 : 0
    )
  }

  @action
  preventVisibleOnce() {
    this.preventVisible = true
  }

  /**
   * 切换登录框功能
   * @param title 登录框功能标题
   * @param state 状态
   */
  @action
  onToggleModal(title, state: ModalState = 'phone') {
    this.visible = true
    this.ModalTitle = title
    this.ModalState = state
  }

  /** 注销弹窗展示不展示 */
  @observable logoutVisible: boolean = false
  /** 注销弹窗里面展示的内容 */
  @observable logoutModalTitle: LogoutType = 'notice'
  /** 放弃的类型  放弃资源 ｜ 放弃课程 */
  @observable abandonChoose: AbandonType = 'course'
  /** 是否点击了放弃课程 */
  @observable clickAbandonCourse: boolean = false
  /** 是否点击了放弃虚拟资源 */
  @observable clickAbandonResource: boolean = false

  /**
   * 切换注销弹窗展示不展示
   * @param visible
   * @returns
   */
  @action
  onToggleLogoutVisible(visible: boolean) {
    this.logoutVisible = visible
    this.logoutModalTitle = 'notice'
    this.abandonChoose = 'course'
    this.onResetLogoutInfo()
  }

  /**
   * 切换弹窗里面的内容
   * @param title
   */
  @action
  onToggleLogoutModal(title: LogoutType) {
    this.logoutVisible = true
    this.logoutModalTitle = title
  }

  @action
  onAgreeAbandonChoose(choose: AbandonType) {
    if (choose === 'course') this.clickAbandonCourse = true
    else this.clickAbandonResource = true
  }

  @action
  onChangeAbandonChoose(choose: AbandonType) {
    this.abandonChoose = choose
  }

  @action
  onResetLogoutInfo() {
    this.clickAbandonCourse = false
    this.clickAbandonResource = false
  }

  @action
  onChangeUserAddress(addressInfo) {
    this.userAddress = addressInfo
  }

  @action
  onChangeLoginModalLoading(loading: boolean) {
    this.isLoginModalLoading = loading
  }

  /**
   * 完善个人信息后跳转支付页面数据
   */
  // @observable
  // toPayMentQuery = {}

  // /**
  //  * 设置完善个人信息后跳转支付页面的数据
  //  */
  // @action
  // onSetToPaymentQuery(data) {
  //   this.toPayMentQuery = data
  // }

  @computed
  get experienceBackground() {
    switch (this.level) {
      case 0:
        return '#E7E7DD'
      case 1:
        return '#EBE0D0'
      case 2:
        return '#EBD9BE'
      case 3:
        return '#E7D4BD'
      case 4:
        return '#E7D2C4'
      case 5:
        return '#E7CEC0'
      case 6:
        return '#E7CCBD'
      case 7:
        return '#E6C8C5'
      case 8:
        return '#E7C7C4'
      case 9:
        return '#E7C4C3'
      case 10:
        return ''
    }
  }
}
